import { ref } from 'vue'
import type { Ref } from 'vue'
import type {
  PublicVolunteerInfo,
  PublicVolunteerSignUp,
  VolunteerSignUpPayload,
} from '@/types/volunteer'
import {
  getInfo as apiGetInfo,
  submitSignUp as apiSubmitSignUp,
} from '@/api/publicVolunteer'

function isValidationError(
  e: unknown,
): e is { response: { status: number; data: { errors: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

function isConflictError(
  e: unknown,
): e is { response: { status: number; data: { full_slot_ids?: number[]; full_role_ids?: number[] } } } {
  return isValidationError(e) && (e as { response: { status: number } }).response.status === 409
}

export function useVolunteerSignUp(): {
  info: Ref<PublicVolunteerInfo | null>
  loading: Ref<boolean>
  errors: Ref<Record<string, string[]>>
  fullSlotIds: Ref<number[]>
  fullRoleIds: Ref<number[]>
  generalError: Ref<string | null>
  fetchInfo: (eventSlug: string) => Promise<void>
  submit: (eventSlug: string, payload: VolunteerSignUpPayload) => Promise<PublicVolunteerSignUp | null>
} {
  const info = ref<PublicVolunteerInfo | null>(null)
  const loading = ref(false)
  const errors = ref<Record<string, string[]>>({})
  const fullSlotIds = ref<number[]>([])
  const fullRoleIds = ref<number[]>([])
  const generalError = ref<string | null>(null)

  async function fetchInfo(eventSlug: string): Promise<void> {
    loading.value = true
    generalError.value = null
    try {
      info.value = await apiGetInfo(eventSlug)
    } catch {
      generalError.value = 'volunteer.wizard.errors.submit_failed'
    } finally {
      loading.value = false
    }
  }

  async function submit(
    eventSlug: string,
    payload: VolunteerSignUpPayload,
  ): Promise<PublicVolunteerSignUp | null> {
    loading.value = true
    errors.value = {}
    fullSlotIds.value = []
    fullRoleIds.value = []
    generalError.value = null

    try {
      const result = await apiSubmitSignUp(eventSlug, payload)
      return result
    } catch (e: unknown) {
      if (isConflictError(e)) {
        fullSlotIds.value = e.response.data.full_slot_ids ?? []
        fullRoleIds.value = e.response.data.full_role_ids ?? []
        return null
      }
      if (isValidationError(e) && e.response.status === 422) {
        errors.value = e.response.data.errors ?? {}
        return null
      }
      generalError.value = 'volunteer.wizard.errors.submit_failed'
      return null
    } finally {
      loading.value = false
    }
  }

  return {
    info,
    loading,
    errors,
    fullSlotIds,
    fullRoleIds,
    generalError,
    fetchInfo,
    submit,
  }
}
